import React, { useState, useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { db, loginAdmin } from "../../Firebase";
import { collection, getDocs, onSnapshot } from "firebase/firestore";

const UserChart = () => {
  const [users, setUsers] = useState([]);
  const [dailyMatches, setDailyMatches] = useState(0);
  const [weeklyMatches, setWeeklyMatches] = useState(0);
  const [monthlyMatches, setMonthlyMatches] = useState(0);

  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    let unsubscribe = null;

    const init = async () => {
      try {
        await loginAdmin();

        // 1. Fetch users (one-time is fine here)
        const usersCollection = collection(db, "users");
        const userSnapshot = await getDocs(usersCollection);
        const usersList = userSnapshot.docs.map(doc => ({
          id: doc.id,
          uid: doc.data().uid || doc.id,
          ...doc.data()
        }));
        setUsers(usersList);

        // 2. Real-time listener on the 'matches' collection
        unsubscribe = onSnapshot(collection(db, "matches"), (querySnapshot) => {
          const now = new Date();
          let daily = 0, weekly = 0, monthly = 0;

          querySnapshot.forEach((doc) => {
            const data = doc.data();

            if (!data.timestamp) return; // skip if serverTimestamp hasn't resolved yet

            let matchDate;
            if (data.timestamp.toDate && typeof data.timestamp.toDate === "function") {
              matchDate = data.timestamp.toDate();
            } else if (data.timestamp.seconds) {
              matchDate = new Date(data.timestamp.seconds * 1000);
            } else {
              matchDate = new Date(data.timestamp);
            }

            const diffTime = Math.abs(now - matchDate);
            const diffDays = diffTime / (1000 * 60 * 60 * 24);

            if (diffDays <= 1) daily++;
            if (diffDays <= 7) weekly++;
            if (diffDays <= 30) monthly++;
          });

          setDailyMatches(daily);
          setWeeklyMatches(weekly);
          setMonthlyMatches(monthly);
        });

      } catch (error) {
        console.error("Error fetching Vortex analytics:", error);
      }
    };

    init();

    return () => {
      if (unsubscribe) unsubscribe(); // clean up listener on unmount
    };
  }, []);

  // ... 

  // Update chart when user data has loaded
  useEffect(() => {
    if (!users.length || !canvasRef.current) return;

    // Destroy previous chart before creating a new one
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const monthlyCounts = countUsersByMonth();
    const labels = [5, 4, 3, 2, 1, 0].map(getMonthName);

    chartInstance.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "User Registrations",
            data: monthlyCounts,
            backgroundColor: "rgba(59, 130, 246, 0.5)", // Sleek blue
            borderColor: "rgba(59, 130, 246, 1)",
            borderWidth: 1.5,
            borderRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              color: "#e5e7eb", // light grey for dark mode
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(255, 255, 255, 0.08)", // subtle dark grid lines
            },
            ticks: {
              color: "#9ca3af",
            },
            title: {
              display: true,
              text: "Number of Users",
              color: "#9ca3af",
            },
          },
          x: {
            grid: {
              color: "rgba(255, 255, 255, 0.08)",
            },
            ticks: {
              color: "#9ca3af",
            },
            title: {
              display: true,
              text: "Month",
              color: "#9ca3af",
            },
          },
        },
      },
    });

    // Cleanup chart on unmount
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [users]);

  const countUsersByMonth = () => {
    const currentDate = new Date();
    const monthlyCounts = Array(6).fill(0);
    users?.forEach((user) => {
      const createdAtDate = new Date(user.account_created_at);
      const diffMonths =
        (currentDate.getFullYear() - createdAtDate.getFullYear()) * 12 +
        (currentDate.getMonth() - createdAtDate.getMonth());
      if (diffMonths < 6) {
        monthlyCounts[5 - diffMonths]++;
      }
    });
    return monthlyCounts;
  };

  const getMonthName = (monthIndex) => {
    const currentDate = new Date();
    const targetDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - monthIndex,
      1
    );
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return monthNames[targetDate.getMonth()];
  };

  return (
    <div style={{
      width: "100%",
      backgroundColor: "#1f2937", // matches FeedChart dark card background
      borderRadius: "18px",
      padding: "24px 20px",
      boxSizing: "border-box",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.25)",
      display: "flex",
      flexDirection: "column",
      gap: "24px",
    }}>
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        paddingBottom: "16px",
      }}>
        <h2 style={{ margin: 0, color: "white", fontSize: "clamp(1.1rem, 3.5vw, 1.5rem)", fontWeight: 600 }}>
          User Registration
        </h2>
        <span style={{
          color: "#3b82f6",
          fontSize: "clamp(1rem, 3vw, 1.3rem)",
          fontWeight: 700,
          background: "rgba(59, 130, 246, 0.15)",
          padding: "4px 14px",
          borderRadius: "9999px",
        }}>
          {users.length} Total Users
        </span>
      </div>

      {/* Matches Grid Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: "16px",
        width: "100%",
      }}>
        {/* Daily Card */}
        <div style={{
          background: "linear-gradient(135deg, #111827, #1f2937)",
          color: "white",
          padding: "16px",
          borderRadius: "12px",
          boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.05), 0 2px 8px rgba(0, 0, 0, 0.15)",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          border: "1px solid rgba(255, 255, 255, 0.05)",
        }}>
          <h4 style={{ margin: 0, fontSize: "0.8rem", color: "#9ca3af", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Daily Matches
          </h4>
          <h2 style={{ margin: 0, fontSize: "1.75rem", color: "#10b981", fontWeight: 700 }}>
            {dailyMatches}
          </h2>
        </div>

        {/* Weekly Card */}
        <div style={{
          background: "linear-gradient(135deg, #111827, #1f2937)",
          color: "white",
          padding: "16px",
          borderRadius: "12px",
          boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.05), 0 2px 8px rgba(0, 0, 0, 0.15)",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          border: "1px solid rgba(255, 255, 255, 0.05)",
        }}>
          <h4 style={{ margin: 0, fontSize: "0.8rem", color: "#9ca3af", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Weekly Matches
          </h4>
          <h2 style={{ margin: 0, fontSize: "1.75rem", color: "#3b82f6", fontWeight: 700 }}>
            {weeklyMatches}
          </h2>
        </div>

        {/* Monthly Card */}
        <div style={{
          background: "linear-gradient(135deg, #111827, #1f2937)",
          color: "white",
          padding: "16px",
          borderRadius: "12px",
          boxShadow: "inset 0 1px 1px rgba(255, 255, 255, 0.05), 0 2px 8px rgba(0, 0, 0, 0.15)",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          border: "1px solid rgba(255, 255, 255, 0.05)",
        }}>
          <h4 style={{ margin: 0, fontSize: "0.8rem", color: "#9ca3af", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Monthly Matches
          </h4>
          <h2 style={{ margin: 0, fontSize: "1.75rem", color: "#f59e0b", fontWeight: 700 }}>
            {monthlyMatches}
          </h2>
        </div>
      </div>

      {/* Responsive Chart Area */}
      <div style={{ width: "100%", height: "260px", position: "relative" }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};

export default UserChart;

